import { useEffect, useEffectEvent, useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import { createTask } from '@/lib/tasks-api';
import { describeApiError } from '@/lib/api-errors';
import { UI_PREFS } from '@/lib/ui-prefs';
import { usePref } from '@/hooks/use-pref';
import { useAuthStore } from '@/stores/authStore';
import { unsavedSubtasks, type PendingSubtask } from './pending-subtask-codec';

export type { PendingSubtask } from './pending-subtask-codec';

/** A sub-task not on the server yet, as its row shows it: still saving, or failed (Retry). */
export interface PendingSubtaskRow extends PendingSubtask {
  saving: boolean;
}

// The saves going in this tab, shared by every open view of the task (the pop-up and the Tasks panel).
const inFlight = new Set<string>();
const listeners = new Set<() => void>();
let inFlightVersion = 0;
function changed() {
  inFlightVersion += 1;
  listeners.forEach((l) => l());
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * SF-T-144: adding a sub-task shows it at once and empties the box, so the next one can be typed. It's
 * kept in the browser until the server has it: a failed save stays in the list as "Not saved" with
 * Retry and Remove, and so does one whose tab closed or reloaded before the save finished. Those are tried
 * again by themselves when the task opens and when the browser comes back online.
 */
export function usePendingSubtasks(parentId: string, savedIds: readonly string[], onSaved: () => Promise<unknown> | void) {
  const me = useAuthStore((s) => s.user?.id);
  const [stored, setStored] = usePref(UI_PREFS.tasksPendingSubtasks, { task: parentId, scopeId: me });
  useSyncExternalStore(subscribe, () => inFlightVersion);

  const save = async (entry: PendingSubtask, { quiet = false }: { quiet?: boolean } = {}) => {
    if (inFlight.has(entry.id)) return;
    inFlight.add(entry.id);
    changed();
    try {
      const res = await createTask({ id: entry.id, title: entry.title, parent_task_id: parentId });
      if (res.data) {
        // Once the task has loaded again with it in the list, so the row never blinks out.
        await Promise.resolve(onSaved()).catch(() => undefined);
        setStored((prev) => prev.filter((p) => p.id !== entry.id && !savedIds.includes(p.id)));
      } else if (!quiet) {
        toast.error(describeApiError(res, `Couldn't save the sub-task "${entry.title}"`), {
          description: 'It stays in the list, marked Not saved: retry it from there.',
        });
      }
    } finally {
      inFlight.delete(entry.id);
      changed();
    }
  };

  const add = (title: string) => {
    const entry: PendingSubtask = { id: crypto.randomUUID(), title, at: new Date().toISOString() };
    setStored((prev) => [...prev, entry]);
    void save(entry);
  };

  const remove = (id: string) => setStored((prev) => prev.filter((p) => p.id !== id));

  // Tried again without asking: once when the task opens, and each time the browser is back online
  // (quietly: the rows still say Not saved, and a person's Retry still toasts).
  const retryUnsaved = useEffectEvent(() => {
    if (!navigator.onLine) return;
    for (const p of unsavedSubtasks(stored, savedIds)) void save(p, { quiet: true });
  });
  useEffect(() => {
    retryUnsaved();
    const online = () => retryUnsaved();
    window.addEventListener('online', online);
    return () => window.removeEventListener('online', online);
  }, [parentId]);

  const rows: PendingSubtaskRow[] = unsavedSubtasks(stored, savedIds).map((p) => ({ ...p, saving: inFlight.has(p.id) }));
  return { rows, add, retry: (row: PendingSubtask) => void save(row), remove };
}
